export type devicesListType = { [key: string]: number };
export type onPromiseAttrType = { devices: devicesListType, current: string, old: string };

/**
 * Manage breakpoints events
 */
const DeviceManager = {
    oldDevice: null,
    currentDevice: null,
    devices: {
        xl: 1220,
        lg: 992,
        md: 768,
        sm: 576,
        xs: 0,
    },
    callbacks: [],
    windowCallbacks: {},

    /**
     * Initialize events
     * 
     * @return {void}
     */
    init() : void {
        document.addEventListener('DOMContentLoaded', () => {
            this.checkDevice().then((attr: onPromiseAttrType) => {
                this.executeCallbacks(attr);
                this.executeWindowCallbacks();
            }).catch(() => null);
        });

        window.addEventListener('resize', () => {
            this.executeWindowCallbacks();

            this.checkDevice().then((attr: onPromiseAttrType) => {
                this.executeCallbacks(attr);
            }).catch(() => null);
        });
    },

    /**
     * Check if device has changed
     * 
     * @return {Promise<onPromiseAttrType>}
     */
    checkDevice() : Promise<onPromiseAttrType> {
        const winW = window.innerWidth;

        return new Promise((resolve, reject) => {
            this.oldDevice = this.currentDevice;

            let breakpointSup = 99999;
            for (const device in this.devices) {
                // Skip loop if the property is from prototype
                if (!Object.prototype.hasOwnProperty.call(this.devices, device)) {
                    continue;
                }
            
                const breakpoint = this.devices[device];

                if (winW >= breakpoint && winW < breakpointSup && this.oldDevice !== device) {
                    this.currentDevice = device;

                    resolve({ devices: this.devices, current: device, old: this.oldDevice });
                }

                breakpointSup = this.devices[device];
            }

            reject();
        });
    },

    /**
     * Get the current device size
     * 
     * @return {number}
     */
    getCurrentDeviceSize() : number {
        return this.devices[this.getCurrentDevice()];
    },
    
    /**
     * Get the current device name
     * 
     * @return {string}
     */
    getCurrentDevice() : string {
        return this.currentDevice;
    },

    /**
     * Add a callback when device change
     * 
     * @param {CallableFunction} fn
     * 
     * @return {void}
     */
    addCallback(fn: CallableFunction) : void {
        this.callbacks.push(fn);

        if (document.body.classList.contains('wp-admin')) {
            this.checkDevice().then((attr: onPromiseAttrType) => {
                fn(attr);
            }).catch(() => null);
        }
    },

    /**
     * Add a callback when window is resized
     * 
     * @param {(string|CallableFunction)[]} args
     * 
     * Usage:
     * addWindowCallback('callback-name', function, arg_1_for_function, arg_2_for_function, ...);
     * 
     * @return {void}
     */
    addWindowCallback(...args: (string|CallableFunction)[]) : void {
        let name = null;
        let fn = null;

        let newArgsBeginAt = 0;

        if (typeof args[0] === 'string') {
            name = args[0];

            newArgsBeginAt = 1;

            if (typeof args[1] === 'function') {
                fn = args[1];

                newArgsBeginAt = 2;
            }
        } else if (typeof args[0] === 'function') {
            fn = args[0];

            newArgsBeginAt = 1;

            let newName = 'callback-' + Object.keys(this.windowCallbacks).length;
            if (this.windowCallbacks[newName]) {
                newName += '1';
            }

            name = newName;
        }

        const newArgs = [];
        for (let i = newArgsBeginAt; i < args.length; i++) {
            newArgs.push(args[i]);
        }

        if (!fn) {
            console.error('Function not defined');
            throw 'Function not defined';
        }

        this.windowCallbacks[name] = {
            fn,
            args: newArgs,
        };

        if (document.body.classList.contains('wp-admin')) {
            this.checkDevice().then((attr: onPromiseAttrType) => {
                fn(attr);
            }).catch(() => null);
        }
    },

    /**
     * Remove a callback from window callbacks array
     * 
     * @param {string} name
     * 
     * @return {void}
     */
    removeWindowCallback(name: string) : void {
        if (this.windowCallbacks[name]) {
            delete this.windowCallbacks[name];
        }
    },

    /**
     * Execute all callbacks
     * 
     * @param {onPromiseAttrType} attr
     * 
     * @return {void}
     */
    executeCallbacks(attr: onPromiseAttrType) : void {
        this.callbacks.forEach((callback: CallableFunction) => {
            callback(attr);
        });
    },

    /**
     * Execute all window callbacks
     * 
     * @return {void}
     */
    executeWindowCallbacks() : void {
        Object.keys(this.windowCallbacks).forEach(key => {
            const callback = this.windowCallbacks[key].fn;
            const args = this.windowCallbacks[key].args;

            callback({ devices: this.devices, current: this.currentDevice, old: this.oldDevice }, ...args);
        });
    },

    /**
     * Check if deviceToCompare is between deviceReferenceMin and deviceReferenceMax
     * 
     * @param {string} deviceToCompare
     * @param {string} deviceReferenceMin
     * @param {string} deviceReferenceMax
     * 
     * @return {boolean}
     */
    isBetween(deviceToCompare: string, deviceReferenceMin: string, deviceReferenceMax: string) : boolean {
        return this.isGreaterThanOrEqual(deviceToCompare, deviceReferenceMin) && this.isLowerThan(deviceToCompare, deviceReferenceMax);
    },

    /**
     * Check if deviceToCompare is greater than or equal deviceReference
     * 
     * @param {string} deviceToCompare
     * @param {string} deviceReference
     * 
     * @return {boolean}
     */
    isGreaterThanOrEqual(deviceToCompare: string, deviceReference: string) : boolean {
        return this.devices[deviceToCompare] >= this.devices[deviceReference];
    },

    /**
     * Check if deviceToCompare is lower than deviceReference
     * 
     * @param {string} deviceToCompare
     * @param {string} deviceReference
     * 
     * @return {boolean}
     */
    isLowerThan(deviceToCompare: string, deviceReference: string) : boolean {
        return this.devices[deviceToCompare] < this.devices[deviceReference];
    },
};

export default DeviceManager;
